// src/hooks/useBankDashboardData.js
// Hook to fetch bank dashboard data from MySQL API

import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

export const useBankDashboardData = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Data from API
  const [dashboardData, setDashboardData] = useState(null);
  const [staff, setStaff] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [transactions, setTransactions] = useState([]);

  const [metrics, setMetrics] = useState({
    activeInvestorCount: 0,
    pendingKycApplications: 0,
    totalSettled: 0,
    kycApprovalRate: 85,
    dealBookValue: 3_033_175_000,  // KES 3.03B
    totalDepositValue: 1_000_000,  // $1M USD
    monthlyYield: 9,               // 9%/month
    fxVolume: 25_000_000,          // Foreign exchange volume
  });
  const [kycPipeline, setKycPipeline] = useState({
    submitted: 0, inReview: 0, approved: 0, rejected: 0, expired: 0,
  });
  const [todayActivity, setTodayActivity] = useState({
    settlements: 3_500_000,
    fxDeals: 2_800_000,
    kycApprovedToday: 8,
    pendingReview: 12,
    newApplications: 8,
    targetSettlements: 5_000_000,
    targetFxVolume: 4_000_000,
    avgDealSize: 22_000,
  });
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [plData, setPlData] = useState({
    grossRevenue:    139_212_000,
    interestIncome:  104_409_000,
    operatingCosts:   20_881_800,
    netProfit:        83_527_200,
    margin:                   60,
    mtdRevenue:       11_600_000,
    mtdTarget:        15_000_000,
  });
  const [depositInstruments, setDepositInstruments] = useState([]);
  const [forwardContracts, setForwardContracts] = useState({
    currentSpotRate:     128.90,
    avgForwardRate:      154.68,
    unrealizedGainKES:   25_778_000,
    totalActive:                 1,
    totalNotionalKES:   127_611_000,
    totalCostKES:         3_190_275,
    hedgeRatio:                100,
  });

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch dashboard summary
      try {
        const dashboard = await api.getDashboard();
        setDashboardData(dashboard);

        // Update metrics from API if available
        if (dashboard.metrics) {
          setMetrics(prev => ({
            ...prev,
            ...dashboard.metrics,
            activeLoanCount: dashboard.metrics.activeLoans || prev?.activeLoanCount || 0,
            pendingApplications: dashboard.metrics.pendingLoans || prev?.pendingApplications || 0,
          }));
        }
      } catch (e) {
        console.warn('Could not fetch dashboard:', e);
      }

      // Fetch staff (users)
      try {
        const usersData = await api.getUsers();
        // Transform API user data to match expected format
        const transformedStaff = usersData.map(user => ({
          id: user.id,
          name: `${user.firstName} ${user.lastName}`,
          email: user.email,
          phone: user.phone || '',
          role: user.role,
          roleDisplay: getRoleDisplay(user.role),
          department: getDepartment(user.role),
          status: user.status,
          lastLogin: user.lastLoginAt,
          createdAt: user.createdAt,
          permissions: []
        }));
        setStaff(transformedStaff);
      } catch (e) {
        console.warn('Could not fetch users, using empty list:', e);
        setStaff([]);
      }

      // Fetch investors (KYC approved clients)
      try {
        const investorsData = await api.getInvestors?.() || [];
        setInvestors(investorsData);
      } catch (e) {
        console.warn('Could not fetch investors:', e);
      }

      // Fetch transactions
      try {
        const txData = await api.getTransactions({ limit: 50 });
        setTransactions(txData);
      } catch (e) {
        console.warn('Could not fetch transactions:', e);
      }

    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Staff CRUD operations
  const addStaff = async (staffData) => {
    try {
      const newUser = await api.createUser({
        firstName: staffData.name.split(' ')[0],
        lastName: staffData.name.split(' ').slice(1).join(' ') || 'User',
        email: staffData.email,
        phone: staffData.phone,
        role: staffData.role,
        password: 'TempPassword123!' // Should be changed on first login
      });
      
      // Add to local state
      const newStaffMember = {
        id: newUser.id,
        name: staffData.name,
        email: staffData.email,
        phone: staffData.phone,
        role: staffData.role,
        roleDisplay: getRoleDisplay(staffData.role),
        department: staffData.department,
        status: 'pending',
        lastLogin: null,
        createdAt: new Date().toISOString(),
        permissions: []
      };
      setStaff(prev => [...prev, newStaffMember]);
      return newStaffMember;
    } catch (err) {
      console.error('Add staff error:', err);
      throw err;
    }
  };

  const updateStaff = async (staffId, staffData) => {
    try {
      await api.updateUser(staffId, {
        firstName: staffData.name.split(' ')[0],
        lastName: staffData.name.split(' ').slice(1).join(' ') || 'User',
        phone: staffData.phone,
        role: staffData.role
      });
      
      // Update local state
      setStaff(prev => prev.map(s => 
        s.id === staffId 
          ? { 
              ...s, 
              ...staffData, 
              roleDisplay: getRoleDisplay(staffData.role) 
            } 
          : s
      ));
    } catch (err) {
      console.error('Update staff error:', err);
      throw err;
    }
  };

  const suspendStaff = async (staffId) => {
    const member = staff.find(s => s.id === staffId);
    const newStatus = member?.status === 'suspended' ? 'active' : 'suspended';
    
    try {
      await api.updateUser(staffId, { status: newStatus });
      setStaff(prev => prev.map(s => 
        s.id === staffId ? { ...s, status: newStatus } : s
      ));
    } catch (err) {
      console.error('Suspend staff error:', err);
      throw err;
    }
  };

  const deleteStaff = async (staffId) => {
    // Note: API might not support delete, so we just remove from local state
    // In production, you'd call api.deleteUser(staffId)
    setStaff(prev => prev.filter(s => s.id !== staffId));
  };

  // Helper functions
  const getRoleDisplay = (role) => {
    const map = {
      platform_admin: 'Platform Administrator',
      bank_admin: 'Bank Administrator',
      bank_treasury_dealer: 'Treasury Dealer',
      bank_forwards_dealer: 'Forwards Dealer',
      bank_compliance_maker: 'Compliance Maker',
      bank_compliance_checker: 'Compliance Checker',
      investor_family_office: 'Family Office Director',
      investor_individual: 'Individual Investor'
    };
    return map[role] || role;
  };

  const getDepartment = (role) => {
    const map = {
      bank_admin: 'Administration',
      bank_treasury_dealer: 'Treasury',
      bank_forwards_dealer: 'Forwards',
      bank_compliance_maker: 'Compliance',
      bank_compliance_checker: 'Compliance'
    };
    return map[role] || 'General';
  };

  return {
    // Loading state
    loading,
    error,
    
    // API data
    dashboardData,
    staff,
    investors,
    transactions,

    // Fallback data (will be replaced as API features are added)
    metrics,
    kycPipeline,
    todayActivity,
    revenueTrend,
    plData,
    depositInstruments,
    forwardContracts,

    // Actions
    refresh: fetchData,
    addStaff,
    updateStaff,
    suspendStaff,
    deleteStaff,

    // Setters for local state updates
    setStaff,
    setMetrics,
    setKycPipeline,
  };
};

export default useBankDashboardData;
